/**
 * Mentor Request Modal Component
 * 
 * Modal for users to request to become a mentor for a cohort.
 */

import { useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Textarea from '@cloudscape-design/components/textarea';
import Alert from '@cloudscape-design/components/alert';

interface MentorRequestModalProps {
  visible: boolean;
  cohortName: string;
  onDismiss: () => void;
  onSubmit: (notes: string) => Promise<void>;
}

export function MentorRequestModal({
  visible,
  cohortName,
  onDismiss,
  onSubmit,
}: MentorRequestModalProps) {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(notes);
      setNotes('');
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit mentor request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    if (!isSubmitting) {
      setNotes('');
      setError(null);
      onDismiss();
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Request to be a Mentor"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Submit Request
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box variant="p">
          You are requesting to become a mentor for <strong>{cohortName}</strong>.
        </Box>

        <Alert type="info" header="What happens next?">
          <SpaceBetween size="xs">
            <Box>1. Your request will be sent to the program administrators</Box>
            <Box>2. Admins will review your qualifications and experience</Box>
            <Box>3. You will be notified when your request is approved or rejected</Box>
            <Box>4. Once approved, you can be assigned mentees by the program admin</Box>
          </SpaceBetween>
        </Alert>

        <FormField
          label="Why do you want to be a mentor?"
          description="Optional: Share your experience and motivation for mentoring"
        >
          <Textarea
            value={notes}
            onChange={({ detail }) => setNotes(detail.value)}
            placeholder="Example: I have 5 years of experience in this area and would like to help guide new participants..."
            rows={6}
            disabled={isSubmitting}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
